import React from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "../../i18n";

/**
 * Feature-level error boundary — catches errors within a feature section
 * and allows the user to retry without reloading the entire page.
 */
class FeatureErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  // Fehlerzustand darf beim Navigieren nicht kleben bleiben: wechselt die
  // Route, bekommt die neue Seite einen frischen Versuch (03.07.2026 —
  // gelatchter Fehler machte nach einem Crash ALLE Folgeseiten "kaputt").
  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  componentDidCatch(error, errorInfo) {
    console.error(`[FeatureErrorBoundary:${this.props.name || "unknown"}]`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[200px] gap-3 p-6">
          <span className="material-symbols-outlined text-4xl text-error">warning</span>
          <p className="text-sm text-on-surface-variant text-center max-w-sm">
            {this.props.descriptionText}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-3 py-1.5 rounded-lg bg-surface-container text-on-surface text-xs font-medium hover:bg-surface-container-high transition-colors"
          >
            {this.props.retryText}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function FeatureErrorBoundary({ children, name }) {
  const { t } = useTranslation();
  const location = useLocation();
  return (
    <FeatureErrorBoundaryClass
      name={name}
      resetKey={location.pathname}
      descriptionText={t("layout.errorBoundary.featureSection")}
      retryText={t("layout.errorBoundary.tryAgain")}
    >
      {children}
    </FeatureErrorBoundaryClass>
  );
}
